// src/components/AddressModal/MiniMap.js
import React, { useEffect, useRef, useState, useCallback } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Maximize2, Loader, MapPin } from 'lucide-react';
import './MiniMap.css';

// Fix Leaflet's default marker icon issue with webpack/CRA bundlers
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
    iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const MiniMap = ({ latitude, longitude, onLocationChange, onExpandMap }) => {
    const mapContainerRef = useRef(null);
    const mapInstanceRef = useRef(null);
    const markerRef = useRef(null);
    const [isLocating, setIsLocating] = useState(false);
    const [isReverseGeocoding, setIsReverseGeocoding] = useState(false);

    const defaultCenter = [17.3850, 78.4867];

    const reverseGeocode = useCallback(async (lat, lng) => {
        setIsReverseGeocoding(true);
        try {
            const res = await fetch(
                `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1&zoom=18`,
                { headers: { 'Accept-Language': 'en' } }
            );
            if (!res.ok) throw new Error('Geocode failed');
            const data = await res.json();
            const addr = data.address || {};

            const addressParts = [
                addr.house_number,
                addr.road || addr.pedestrian,
                addr.neighbourhood,
                addr.suburb,
                addr.city_district || addr.district
            ].filter(Boolean);

            let streetAddress = addressParts.join(', ');
            if (streetAddress.length < 10 && data.display_name) {
                const displayParts = data.display_name.split(',').map(s => s.trim());
                streetAddress = displayParts.slice(0, 4).join(', ');
            }

            const structured = {
                display_name: data.display_name || '',
                address: streetAddress,
                apartment: addr.building || addr.house_number || '',
                city: addr.city || addr.town || addr.village || addr.state_district || addr.county || '',
                state: addr.state || '',
                zip_code: addr.postcode || '',
                country: addr.country || 'India',
                latitude: lat,
                longitude: lng
            };
            onLocationChange(structured);
        } catch (e) {
            console.warn('Reverse geocode failed:', e);
            onLocationChange({ latitude: lat, longitude: lng });
        } finally {
            setIsReverseGeocoding(false);
        }
    }, [onLocationChange]);

    useEffect(() => {
        if (!mapContainerRef.current || mapInstanceRef.current) return;

        const hasLocation = !!(latitude && longitude);
        const startLat = hasLocation ? parseFloat(latitude) : defaultCenter[0];
        const startLng = hasLocation ? parseFloat(longitude) : defaultCenter[1];

        const map = L.map(mapContainerRef.current, {
            center: [startLat, startLng],
            zoom: 16,
            zoomControl: false, // Minimized UI
            dragging: true,
            scrollWheelZoom: false,
        });

        // Add OpenStreetMap tiles
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
            maxZoom: 19,
        }).addTo(map);

        const marker = L.marker([startLat, startLng], {
            draggable: true,
            autoPan: true
        }).addTo(map);

        marker.on('dragend', () => {
            const pos = marker.getLatLng();
            reverseGeocode(pos.lat, pos.lng);
        });

        map.on('click', (e) => {
            marker.setLatLng(e.latlng);
            reverseGeocode(e.latlng.lat, e.latlng.lng);
        });

        mapInstanceRef.current = map;
        markerRef.current = marker;

        const rafId = requestAnimationFrame(() => {
            setTimeout(() => {
                try {
                    if (mapInstanceRef.current) mapInstanceRef.current.invalidateSize();
                } catch (e) { }
            }, 100);
        });

        // Try geolocation on first load if no initial lat/lng
        if (!hasLocation && navigator.geolocation) {
            setIsLocating(true);
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const { latitude: lLat, longitude: lLng } = position.coords;
                    if (mapInstanceRef.current && markerRef.current) {
                        const latlng = L.latLng(lLat, lLng);
                        markerRef.current.setLatLng(latlng);
                        mapInstanceRef.current.setView(latlng, 17);
                        reverseGeocode(lLat, lLng);
                    }
                    setIsLocating(false);
                },
                (err) => {
                    console.warn('Auto-detect location failed:', err.message);
                    setIsLocating(false);
                },
                { enableHighAccuracy: true, timeout: 8000, maximumAge: 0 }
            );
        }

        return () => {
            cancelAnimationFrame(rafId);
            map.remove();
            mapInstanceRef.current = null;
            markerRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Effect to update map if latitude/longitude change externally (e.g. MapPicker closed)
    useEffect(() => {
        if (mapInstanceRef.current && markerRef.current && latitude && longitude) {
            const currentPos = markerRef.current.getLatLng();
            if (currentPos.lat !== parseFloat(latitude) || currentPos.lng !== parseFloat(longitude)) {
                const latlng = L.latLng(latitude, longitude);
                markerRef.current.setLatLng(latlng);
                mapInstanceRef.current.setView(latlng, mapInstanceRef.current.getZoom());
            }
        }
    }, [latitude, longitude]);

    return (
        <div className="mini-map-wrapper">
            <div className="mini-map-container" ref={mapContainerRef}></div>

            {/* Overlay buttons / loaders */}
            {(isLocating || isReverseGeocoding) && (
                <div className="mini-map-loader-overlay">
                    <Loader className="mini-map-spinner" size={20} />
                    <span>{isLocating ? 'Locating...' : 'Fetching address...'}</span>
                </div>
            )}

            <button
                type="button"
                className="mini-map-expand-btn"
                onClick={(e) => { e.preventDefault(); onExpandMap(); }}
                title="Expand on Map"
            >
                <Maximize2 size={16} />
                <span>Expand on Map</span>
            </button>
            <div className="mini-map-drag-hint">
                Drag marker to adjust
            </div>
        </div>
    );
};

export default MiniMap;
